import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'curiosidades',
  author: 'Ivan Henrique',
  publishedAt: '2026-03-15',
  coverImage: '/blog/recordes-brasileirao.svg',
  tags: ['Brasileirão', 'Recordes', 'História', 'Estatísticas'],
  i18n: {
    'pt-BR': {
      slug: 'recordes-brasileirao',
      title: 'Os 10 maiores recordes do Campeonato Brasileiro',
      excerpt:
        'Goleadas absurdas, sequências invictas, artilharias impossíveis. O Brasileirão tem mais de um século de história e marcas que parecem inquebráveis. Veja 10 delas.',
      readTimeMinutes: 6,
      coverImageAlt: 'Capa: 10 maiores recordes do Campeonato Brasileiro',
      keywords: ['recordes brasileirão', 'goleadas brasileiro', 'maior pontuação', 'sequência invicta'],
      content: (
        <>
          <p>
            O Campeonato Brasileiro tem mais de um século de história quando
            consideramos todas as suas fases (Taça Brasil, Robertão,
            Brasileirão moderno). E em mais de 100 anos, surgiram marcas
            impressionantes que entraram para a lenda. Goleadas absurdas,
            sequências invictas, artilharias que parecem impossíveis hoje.
          </p>
          <p>
            Neste artigo, a gente comenta 10 categorias de recordes do
            futebol brasileiro — sem números muito específicos (que mudam
            a cada temporada), mas com o contexto que importa.
          </p>

          <h2>1. Maior goleada da história</h2>
          <p>
            O Brasileirão já viu goleadas históricas — placares como 10x1
            e 7x1 em jogos de fase eliminatória de torneios brasileiros.
            <strong>Goleadas dessa magnitude geralmente acontecem em jogos
            de fase de grupos onde um time já estava classificado e poupou
            titulares</strong>, ou em situações excepcionais de jogo onde
            tudo deu errado pro time que sofreu a goleada.
          </p>

          <h2>2. Sequência invicta mais longa</h2>
          <p>
            Algumas equipes já conseguiram ficar dezenas de jogos sem perder
            — uma combinação rara de talento, sorte e regularidade.
            <strong>Sequências dessa magnitude (20+, 30+, 40+ jogos sem
            derrota) só foram batidas por times que dominavam o esporte
            brasileiro em uma era</strong>.
          </p>

          <h2>3. Maior pontuação em um Brasileirão</h2>
          <p>
            Em 38 rodadas a pontuação máxima possível é 114 (38 vitórias x
            3 pontos). Já tivemos campeões que se aproximaram disso, mas
            nenhum chegou. <strong>Campeões com 80+ pontos são considerados
            excepcionais</strong>; campeões com 70+ pontos são uma marca
            respeitável.
          </p>

          <h2>4. Maior artilheiro de uma única edição</h2>
          <p>
            Artilheiros do Brasileirão historicamente marcam entre 20 e 25
            gols em uma temporada. <strong>Marcas acima de 30 gols são
            raras</strong> e geralmente associadas a atacantes considerados
            ídolos absolutos do esporte brasileiro.
          </p>

          <h2>5. Maior público em um jogo</h2>
          <p>
            Jogos no Maracanã antigo, no formato com 2 turnos e mata-mata,
            chegavam a registrar públicos absurdos — <strong>mais de
            180 mil pessoas em finais históricas</strong> (caso da final
            Brasil x Uruguai na Copa de 1950, embora não seja Brasileirão).
            Em Brasileirão moderno, com estádios reduzidos por questões de
            segurança, os recordes de público giram em torno de 80 mil em
            decisões.
          </p>

          <h2>6. Mais títulos brasileiros</h2>
          <p>
            A disputa pelo título de &quot;maior campeão brasileiro&quot; é
            antiga e gera polêmica. Dependendo do critério (considera-se
            Taça Brasil? Robertão? só era moderna?), os clubes com mais
            títulos variam. <strong>O fato é que o futebol nacional tem
            uma elite reduzida que concentra a maior parte dos
            títulos</strong>: Flamengo, Palmeiras, Santos, Corinthians, São
            Paulo, Cruzeiro e poucos outros.
          </p>

          <h2>7. Maior reviravolta em uma temporada</h2>
          <p>
            Há histórias de times que estavam em últimos lugares e se
            recuperaram pra terminar no meio da tabela. Ou times que
            estavam liderando e despencaram pro rebaixamento. <strong>O
            Brasileirão é particularmente conhecido por essas
            reviravoltas dramáticas no final de temporada</strong>.
          </p>

          <h2>8. Maior rebaixamento histórico</h2>
          <p>
            Times tradicionais que vivenciaram rebaixamento traumático
            fazem parte do folclore do futebol nacional. <strong>Casos
            como o do Cruzeiro em 2019, Palmeiras em 2003 e Corinthians
            em 2007 ainda são lembrados com peso</strong> — clubes do
            primeiro escalão que disputaram a Série B.
          </p>

          <h2>9. Mais convocações para Seleção Brasileira saindo do Brasileirão</h2>
          <p>
            Jogadores do Brasileirão são frequentemente convocados pra
            Seleção. Algumas eras (anos 60-70-80) tinham praticamente
            todos os convocados atuando no Brasil. <strong>Hoje é mais
            raro, pela maior presença de brasileiros no futebol
            europeu</strong>, mas ainda é normal vermos 3-5 brasileiros
            do Brasileirão nas listas oficiais.
          </p>

          <h2>10. Recorde de público presente em estádio</h2>
          <p>
            Maracanã, Mineirão, Beira-Rio, Arena Corinthians, Arena Castelão.
            Esses estádios reúnem multidões. <strong>Públicos médios acima
            de 50 mil em jogos importantes são considerados
            excepcionais</strong> no Brasileirão atual.
          </p>

          <h2>O que isso significa pra quem joga bolão</h2>
          <p>
            Conhecer recordes do Brasileirão não te ajuda diretamente a
            acertar palpites — mas ajuda a ter <strong>perspectiva</strong>
            sobre o que é normal e o que é excepcional. Quando você palpita
            4x0 ou 5x0 no Brasileirão, lembre: goleadas dessa magnitude
            acontecem em poucos jogos por temporada. <strong>Não é o
            padrão</strong>.
          </p>
          <p>
            Da mesma forma, sequências longas de vitórias são raras.
            Mesmo o melhor time da temporada raramente vence 10 jogos
            seguidos. Não palpite no pressuposto de que um time vai
            manter um pico de forma indefinidamente.
          </p>

          <h2>Conclusão</h2>
          <p>
            O Brasileirão tem mais de um século de história e marcas
            impressionantes que se acumulam ano após ano. Conhecer essas
            marcas dá perspectiva ao palpiteiro e enriquece a experiência
            do bolão. No fim do dia, futebol é também sobre as histórias
            — e o Brasileirão tem várias delas pra contar.
          </p>
          <p>
            Veja todos os artigos no <a href="/blog">blog</a> ou volte para o{' '}
            <a href="/bolao-brasileirao">bolão do Brasileirão</a>.
          </p>
        </>
      ),
    },
    en: {
      slug: 'brazilian-league-records',
      title: 'The 10 biggest records of the Brazilian league',
      excerpt:
        'Absurd thrashings, unbeaten runs, impossible goalscoring tallies. The Brasileirão has over a century of history and marks that look unbreakable. See 10 of them.',
      readTimeMinutes: 6,
      coverImageAlt: 'Cover: the 10 biggest records of the Brazilian league',
      coverImage: '/blog/recordes-brasileirao-en.svg',
      keywords: ['brazilian league records', 'brasileirão thrashings', 'highest points total', 'unbeaten run'],
      content: (
        <>
          <p>
            The Brazilian league has over a century of history when we
            consider all of its phases (Taça Brasil, Robertão, the modern
            Brasileirão). And in more than 100 years, impressive marks have
            emerged that have entered legend. Absurd thrashings,
            unbeaten runs, goalscoring tallies that look impossible today.
          </p>
          <p>
            In this article, we go through 10 categories of records from
            Brazilian football — without very specific numbers (which change
            every season), but with the context that matters.
          </p>

          <h2>1. The biggest thrashing in history</h2>
          <p>
            The Brazilian league has seen historic thrashings — scores like
            10-1 and 7-1 in knockout matches of Brazilian tournaments.
            <strong>Thrashings of this magnitude usually happen in
            group-stage matches where a team had already qualified and rested
            its starters</strong>, or in exceptional match situations where
            everything went wrong for the team on the receiving end.
          </p>

          <h2>2. The longest unbeaten run</h2>
          <p>
            Some teams have managed to go dozens of matches without losing
            — a rare combination of talent, luck and consistency.
            <strong>Runs of this magnitude (20+, 30+, 40+ matches without
            a loss) have only been set by teams that dominated Brazilian
            football in an era</strong>.
          </p>

          <h2>3. The highest points total in a Brasileirão</h2>
          <p>
            Over 38 matchdays the maximum possible points total is 114 (38
            wins x 3 points). We have had champions who came close to that,
            but none reached it. <strong>Champions with 80+ points are
            considered exceptional</strong>; champions with 70+ points are a
            respectable mark.
          </p>

          <h2>4. The biggest top scorer of a single edition</h2>
          <p>
            Top scorers of the Brazilian league historically score between 20
            and 25 goals in a season. <strong>Marks above 30 goals are
            rare</strong> and are usually associated with forwards regarded as
            absolute idols of Brazilian football.
          </p>

          <h2>5. The biggest attendance at a match</h2>
          <p>
            Matches at the old Maracanã, in the format with two legs and
            knockout stages, would record absurd attendances — <strong>more
            than 180,000 people at historic finals</strong> (the case of the
            Brazil v Uruguay final at the 1950 World Cup, even though it is not
            the Brazilian league). In the modern Brasileirão, with stadium
            capacities reduced for safety reasons, the attendance records hover
            around 80,000 at deciding matches.
          </p>

          <h2>6. The most Brazilian titles</h2>
          <p>
            The dispute over the title of &quot;biggest Brazilian
            champion&quot; is old and stirs controversy. Depending on the
            criterion (do you count the Taça Brasil? the Robertão? only the
            modern era?), the clubs with the most titles vary. <strong>The fact
            is that national football has a small elite that holds most of the
            titles</strong>: Flamengo, Palmeiras, Santos, Corinthians, São
            Paulo, Cruzeiro and a few others.
          </p>

          <h2>7. The biggest turnaround in a season</h2>
          <p>
            There are stories of teams that were in the bottom places and
            recovered to finish mid-table. Or teams that were leading and
            collapsed into relegation. <strong>The Brazilian league is
            particularly known for these dramatic turnarounds at the end of
            the season</strong>.
          </p>

          <h2>8. The biggest historic relegation</h2>
          <p>
            Traditional teams that went through a traumatic relegation are
            part of the folklore of national football. <strong>Cases like
            Cruzeiro in 2019, Palmeiras in 2003 and Corinthians in 2007 are
            still remembered with weight</strong> — top-tier clubs that played
            in Série B.
          </p>

          <h2>9. The most call-ups to the Brazil national team from the Brasileirão</h2>
          <p>
            Players from the Brazilian league are frequently called up to the
            national team. Some eras (the 60s, 70s and 80s) had practically all
            the call-ups playing in Brazil. <strong>Today it is rarer, because
            of the greater presence of Brazilians in European
            football</strong>, but it is still normal to see 3-5 Brazilians
            from the Brazilian league on the official lists.
          </p>

          <h2>10. The record for attendance present at a stadium</h2>
          <p>
            Maracanã, Mineirão, Beira-Rio, Arena Corinthians, Arena Castelão.
            These stadiums gather crowds. <strong>Average attendances above
            50,000 at important matches are considered exceptional</strong> in
            today's Brazilian league.
          </p>

          <h2>What this means for anyone who plays a pool</h2>
          <p>
            Knowing the records of the Brazilian league does not directly help
            you get predictions right — but it helps you have
            <strong>perspective</strong> about what is normal and what is
            exceptional. When you predict 4-0 or 5-0 in the Brazilian league,
            remember: thrashings of this magnitude happen in just a few matches
            per season. <strong>It is not the norm</strong>.
          </p>
          <p>
            In the same way, long winning runs are rare. Even the best team of
            the season rarely wins 10 matches in a row. Do not make predictions
            on the assumption that a team will keep a peak of form
            indefinitely.
          </p>

          <h2>Conclusion</h2>
          <p>
            The Brazilian league has over a century of history and impressive
            marks that pile up year after year. Knowing those marks gives the
            predictor perspective and enriches the pool experience. At the end
            of the day, football is also about the stories — and the Brazilian
            league has plenty of them to tell.
          </p>
          <p>
            See all articles on the <a href="/en/blog">blog</a> or go back to the{' '}
            <a href="/en/brazilian-league-pool">Brazilian league pool</a>.
          </p>
        </>
      ),
    },
    es: {
      slug: 'records-del-brasileirao',
      title: 'Los 10 mayores récords del Brasileirão',
      excerpt:
        'Goleadas absurdas, rachas invictas, cifras goleadoras imposibles. El Brasileirão tiene más de un siglo de historia y marcas que parecen inquebrables. Mira 10 de ellas.',
      readTimeMinutes: 6,
      coverImageAlt: 'Portada: los 10 mayores récords del Brasileirão',
      coverImage: '/blog/recordes-brasileirao-es.svg',
      keywords: ['récords brasileirão', 'goleadas brasileiro', 'mayor puntuación', 'racha invicta'],
      content: (
        <>
          <p>
            El Brasileirão tiene más de un siglo de historia cuando
            consideramos todas sus fases (Taça Brasil, Robertão,
            Brasileirão moderno). Y en más de 100 años, surgieron marcas
            impresionantes que entraron en la leyenda. Goleadas absurdas,
            rachas invictas, cifras goleadoras que parecen imposibles hoy.
          </p>
          <p>
            En este artículo comentamos 10 categorías de récords del
            fútbol brasileño — sin números muy específicos (que cambian
            cada temporada), pero con el contexto que importa.
          </p>

          <h2>1. La mayor goleada de la historia</h2>
          <p>
            El Brasileirão ya vio goleadas históricas — marcadores como 10-1
            y 7-1 en partidos de fase eliminatoria de torneos brasileños.
            <strong>Goleadas de esa magnitud generalmente ocurren en partidos
            de fase de grupos donde un equipo ya estaba clasificado y reservó
            titulares</strong>, o en situaciones excepcionales de partido donde
            todo salió mal para el equipo que sufrió la goleada.
          </p>

          <h2>2. La racha invicta más larga</h2>
          <p>
            Algunos equipos ya lograron quedarse decenas de partidos sin perder
            — una combinación rara de talento, suerte y regularidad.
            <strong>Rachas de esa magnitud (20+, 30+, 40+ partidos sin
            derrota) solo fueron alcanzadas por equipos que dominaban el deporte
            brasileño en una era</strong>.
          </p>

          <h2>3. La mayor puntuación en un Brasileirão</h2>
          <p>
            En 38 jornadas la puntuación máxima posible es 114 (38 victorias x
            3 puntos). Ya tuvimos campeones que se acercaron a eso, pero
            ninguno llegó. <strong>Los campeones con 80+ puntos se consideran
            excepcionales</strong>; los campeones con 70+ puntos son una marca
            respetable.
          </p>

          <h2>4. El mayor goleador de una sola edición</h2>
          <p>
            Los goleadores del Brasileirão históricamente marcan entre 20 y 25
            goles en una temporada. <strong>Las marcas por encima de 30 goles son
            raras</strong> y generalmente se asocian a delanteros considerados
            ídolos absolutos del deporte brasileño.
          </p>

          <h2>5. La mayor asistencia en un partido</h2>
          <p>
            Los partidos en el viejo Maracanã, en el formato con 2 vueltas y
            eliminatoria, llegaban a registrar asistencias absurdas — <strong>más
            de 180 mil personas en finales históricas</strong> (el caso de la
            final Brasil v Uruguay en el Mundial de 1950, aunque no sea
            Brasileirão). En el Brasileirão moderno, con estadios reducidos por
            cuestiones de seguridad, los récords de asistencia giran en torno a
            los 80 mil en las decisivas.
          </p>

          <h2>6. La mayor cantidad de títulos brasileños</h2>
          <p>
            La disputa por el título de &quot;mayor campeón brasileño&quot; es
            antigua y genera polémica. Dependiendo del criterio (¿se considera
            la Taça Brasil? ¿el Robertão? ¿solo la era moderna?), los clubes con
            más títulos varían. <strong>El hecho es que el fútbol nacional tiene
            una élite reducida que concentra la mayor parte de los
            títulos</strong>: Flamengo, Palmeiras, Santos, Corinthians, São
            Paulo, Cruzeiro y pocos otros.
          </p>

          <h2>7. El mayor vuelco en una temporada</h2>
          <p>
            Hay historias de equipos que estaban en los últimos lugares y se
            recuperaron para terminar en el medio de la tabla. O equipos que
            estaban liderando y se desplomaron al descenso. <strong>El
            Brasileirão es particularmente conocido por esos
            vuelcos dramáticos al final de la temporada</strong>.
          </p>

          <h2>8. El mayor descenso histórico</h2>
          <p>
            Los equipos tradicionales que vivieron un descenso traumático
            forman parte del folclore del fútbol nacional. <strong>Casos
            como el de Cruzeiro en 2019, Palmeiras en 2003 y Corinthians
            en 2007 todavía se recuerdan con peso</strong> — clubes del
            primer escalón que jugaron la Serie B.
          </p>

          <h2>9. La mayor cantidad de convocatorias a la selección brasileña desde el Brasileirão</h2>
          <p>
            Los jugadores del Brasileirão son convocados con frecuencia a la
            selección. Algunas eras (los años 60-70-80) tenían prácticamente
            a todos los convocados jugando en Brasil. <strong>Hoy es más
            raro, por la mayor presencia de brasileños en el fútbol
            europeo</strong>, pero todavía es normal ver 3-5 brasileños
            del Brasileirão en las listas oficiales.
          </p>

          <h2>10. El récord de asistencia presente en un estadio</h2>
          <p>
            Maracanã, Mineirão, Beira-Rio, Arena Corinthians, Arena Castelão.
            Esos estadios reúnen multitudes. <strong>Las asistencias medias por
            encima de los 50 mil en partidos importantes se consideran
            excepcionales</strong> en el Brasileirão actual.
          </p>

          <h2>Lo que esto significa para quien juega una quiniela</h2>
          <p>
            Conocer los récords del Brasileirão no te ayuda directamente a
            acertar pronósticos — pero ayuda a tener <strong>perspectiva</strong>
            sobre lo que es normal y lo que es excepcional. Cuando pronosticas
            4-0 o 5-0 en el Brasileirão, recuerda: las goleadas de esa magnitud
            ocurren en pocos partidos por temporada. <strong>No es el
            estándar</strong>.
          </p>
          <p>
            De la misma forma, las rachas largas de victorias son raras.
            Incluso el mejor equipo de la temporada rara vez gana 10 partidos
            seguidos. No pronostiques bajo el supuesto de que un equipo va a
            mantener un pico de forma indefinidamente.
          </p>

          <h2>Conclusión</h2>
          <p>
            El Brasileirão tiene más de un siglo de historia y marcas
            impresionantes que se acumulan año tras año. Conocer esas
            marcas da perspectiva al pronosticador y enriquece la experiencia
            de la quiniela. Al final del día, el fútbol también trata de las
            historias — y el Brasileirão tiene varias para contar.
          </p>
          <p>
            Mira todos los artículos en el <a href="/es/blog">blog</a> o vuelve a la{' '}
            <a href="/es/quiniela-brasileira">quiniela del Brasileirão</a>.
          </p>
        </>
      ),
    },
  },
};
